import { useEffect, useState } from "react";
import { Zap, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

/**
 * One-time TopStep onboarding popup.
 *
 * Renders when:
 *   - The signed-in user has `topstep_enabled === true` (admin granted access)
 *   - AND they haven't connected TopStep yet (`topstep_connected === false`)
 *   - AND they haven't dismissed it yet (`topstep_onboarding_seen === false`)
 *
 * Backed by a per-user server flag so the popup doesn't loop across refreshes /
 * different devices. Dismissed via POST /auth/topstep-onboarding-seen.
 */
export default function TopStepOnboardingPopup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const eligible =
    !!user &&
    user.topstep_enabled === true &&
    user.topstep_connected === false &&
    user.topstep_onboarding_seen === false &&
    !dismissed;

  // Never render this popup on admin routes.
  const onAdminRoute = typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (eligible && !onAdminRoute) {
      // Freeze scroll while the modal is open.
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [eligible, onAdminRoute]);

  if (!eligible || onAdminRoute) return null;

  const markSeen = async () => {
    try { await api.post("/auth/topstep-onboarding-seen"); } catch { /* ignore */ }
    setDismissed(true);
    // Refresh user object so the flag propagates without a hard reload.
    if (refresh) refresh();
  };

  const goToBrokers = async () => {
    await markSeen();
    navigate("/app/brokers");
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="topstep-onboarding-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="topstep-onboarding-title"
    >
      <div className="relative w-full max-w-lg bg-[#121214] border border-amber-400/30 rounded-sm shadow-2xl">
        <button
          onClick={markSeen}
          data-testid="topstep-onboarding-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <Zap className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-widest font-mono">New capability unlocked</span>
          </div>
          <h2
            id="topstep-onboarding-title"
            className="text-2xl font-medium tracking-tight text-zinc-100"
          >
            Now you can automate your TopStep&nbsp;futures account with BonBot.
          </h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Your account has been granted access to trade{" "}
            <span className="text-amber-300 font-medium">
              GC · MGC · NQ · MNQ
            </span>{" "}
            futures through the TopStep / ProjectX Gateway API — with the same 6 automated
            strategies you already use for MT5.
          </p>

          <ul className="text-sm text-zinc-400 space-y-2 pl-1">
            <li className="flex gap-2">
              <span className="text-amber-400 mt-0.5">→</span>
              <span>Connect your TopStep account with your <span className="text-zinc-200">TopstepX username</span> and <span className="text-zinc-200">ProjectX API key</span>.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 mt-0.5">→</span>
              <span>Pick which futures to trade, how many contracts, and enter <span className="text-zinc-200">SL / TP in dollars per contract</span>.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-400 mt-0.5">→</span>
              <span>All existing risk controls (max drawdown, break-even, dollar-BE) apply automatically.</span>
            </li>
          </ul>

          <div className="text-[11px] text-zinc-500 border-l-2 border-amber-400/40 pl-3 py-1">
            You need an active <span className="text-zinc-300">ProjectX API subscription</span> ($29/mo — use code <span className="text-amber-300">topstep</span> for 50% off). Generate the key in TopstepX → Settings → API.
          </div>
        </div>

        <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-2">
          <button
            onClick={markSeen}
            data-testid="topstep-onboarding-dismiss"
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-300 px-3 py-2"
          >
            Maybe later
          </button>
          <button
            onClick={goToBrokers}
            data-testid="topstep-onboarding-connect"
            className="bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium px-4 py-2 rounded-sm"
          >
            Connect TopStep now →
          </button>
        </div>
      </div>
    </div>
  );
}
